import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { toast } from 'sonner';
import { Loader2, Phone, RefreshCw, CheckCircle } from 'lucide-react';

export default function VerifyPhonePage() {
  const { user, verifyPhone, resendCode, isVerified } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [code, setCode] = useState('');

  const handleVerify = async () => {
    if (code.length !== 6) {
      toast.error('Ingresa el código completo de 6 dígitos');
      return;
    }

    setLoading(true);
    try {
      await verifyPhone(code);
      toast.success('¡Teléfono verificado!');
      navigate('/');
    } catch (error) {
      const message = error.response?.data?.detail || 'Código incorrecto';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      const response = await resendCode();
      toast.success('Código reenviado');
      // Show debug code in development
      if (response.debug_code) {
        console.log('Debug code:', response.debug_code);
        toast.info(`[DEV] Código: ${response.debug_code}`, { duration: 10000 });
      }
    } catch (error) {
      toast.error('Error al reenviar código');
    } finally {
      setResending(false);
    }
  };

  if (isVerified) {
    return (
      <div className="page-container pt-12">
        <Card className="card-base text-center py-12" data-testid="already-verified">
          <CheckCircle className="w-16 h-16 text-success mx-auto mb-4" />
          <h2 className="heading-3 mb-2">¡Ya estás verificado!</h2>
          <p className="text-muted-foreground mb-6">Tu teléfono ya fue verificado.</p>
          <Button onClick={() => navigate('/')} className="btn-primary" data-testid="go-home-btn">
            Ir al Inicio
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="page-container pt-12">
      <Card className="card-base" data-testid="verify-phone-card">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Phone className="w-8 h-8 text-primary" />
          </div>
          <CardTitle className="heading-3">Verifica tu Teléfono</CardTitle>
          <CardDescription className="text-base">
            Enviamos un código de 6 dígitos a
            <br />
            <span className="font-medium text-foreground">{user?.phone}</span>
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="flex justify-center">
            <InputOTP
              maxLength={6}
              value={code}
              onChange={setCode}
              data-testid="otp-input"
            >
              <InputOTPGroup>
                <InputOTPSlot index={0} className="w-12 h-14 text-xl" />
                <InputOTPSlot index={1} className="w-12 h-14 text-xl" />
                <InputOTPSlot index={2} className="w-12 h-14 text-xl" />
                <InputOTPSlot index={3} className="w-12 h-14 text-xl" />
                <InputOTPSlot index={4} className="w-12 h-14 text-xl" />
                <InputOTPSlot index={5} className="w-12 h-14 text-xl" />
              </InputOTPGroup>
            </InputOTP>
          </div>

          <Button
            onClick={handleVerify}
            className="w-full btn-primary h-12"
            disabled={loading || code.length !== 6}
            data-testid="verify-submit"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              'Verificar Código'
            )}
          </Button>

          <div className="text-center">
            <p className="text-sm text-muted-foreground mb-2">
              ¿No recibiste el código?
            </p>
            <Button
              variant="ghost"
              onClick={handleResend}
              disabled={resending}
              className="text-primary"
              data-testid="resend-code-btn"
            >
              {resending ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Reenviar código
            </Button>
          </div>

          <p className="text-xs text-center text-muted-foreground">
            El código expira en 10 minutos
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
